import { logStorage, logApiResponse, logError } from '../logger';
import { getLoginStorageData, setLoginStorageData, clearLoginStorageData } from './loginApiHelper';
import { refreshTokens } from './api-controllers/login-controller';

/**
 * Flag to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh the access token using the stored refresh token
 * Updates both tokens in localStorage upon success
 * Returns true if refresh was successful, false otherwise
 */
export const performTokenRefresh = async (): Promise<boolean> => {
  // If already refreshing, wait for that promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = performRefreshInternal();
  const result = await refreshPromise;
  isRefreshing = false;
  refreshPromise = null;
  return result;
};

async function performRefreshInternal(): Promise<boolean> {
  try {
    const loginData = getLoginStorageData();

    if (!loginData?.refreshToken) {
      logStorage('tokenRefresh:no-refresh-token');
      return false;
    }

    logStorage('tokenRefresh:attempting', {
      hasRefreshToken: Boolean(loginData.refreshToken),
    });

    const response = await refreshTokens(loginData.refreshToken);

    if (!response.success || !response.data) {
      logError(`Token refresh failed: ${response.error?.message}`);
      // Clear storage on refresh failure (session likely expired)
      clearTokensOnRefreshFailure();
      return false;
    }

    const { accessToken, refreshToken } = response.data;

    const newLoginData = {
      ...loginData,
      accessToken,
      refreshToken,
    };

    setLoginStorageData(newLoginData);
    logApiResponse('POST', 'api/v2/auth/refresh', response.durationMs);
    logStorage('tokenRefresh:success');

    return true;
  } catch (error) {
    logError(`Token refresh error: ${error}`);
    clearTokensOnRefreshFailure();
    return false;
  }
}

/**
 * Clear tokens when refresh fails (prevents infinite retry loops)
 */
function clearTokensOnRefreshFailure(): void {
  clearLoginStorageData();
  logStorage('tokenRefresh:cleared-tokens');
}

/**
 * Check if the error is a 401 Unauthorized (token expired/invalid)
 */
export const is401Error = (error: any): boolean => {
  return error?.status === 401;
};

/**
 * Check if we should retry with token refresh
 * Returns true if conditions are met for retry
 */
export const shouldRetryWithTokenRefresh = (error: any): boolean => {
  if (!is401Error(error)) {
    return false;
  }

  const loginData = getLoginStorageData();
  return Boolean(loginData?.refreshToken);
};
